// ===========================================
// DIAGNOSTIC OVERLAY - 4-LAYER EVENT VIEWER
// ===========================================
// Toggle with Ctrl+Shift+D
// Shows real-time diagnostic log and analysis
// ===========================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { diagLog, type DiagnosticEntry } from '../utils/diagnosticLogger';

export function DiagnosticOverlay() {
  const [isVisible, setIsVisible] = useState(false);
  const [entries, setEntries] = useState<DiagnosticEntry[]>([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const logRef = useRef<HTMLDivElement>(null);

  // Toggle visibility with Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsVisible(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Poll for new entries when visible
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setEntries(diagLog.getEntries());
    }, 200);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [entries, autoScroll]);

  const handleClear = useCallback(() => {
    diagLog.clear();
    setEntries([]);
  }, []);

  const handleExport = useCallback(() => {
    const text = diagLog.exportText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostic-log-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleAnalyze = useCallback(() => {
    const summary = diagLog.summary();
    console.log('\n========== DIAGNOSTIC SUMMARY ==========');
    console.log('Total entries:', summary.totalEntries);
    console.log('By layer:', summary.byLayer);
    console.log('By round:', summary.byRound);
    if (summary.anomalies.length > 0) {
      console.log('ANOMALIES DETECTED:');
      summary.anomalies.forEach(a => console.log('  -', a));
    }
    console.log('=========================================\n');
    alert(`Summary logged to console.\n\nAnomalies: ${summary.anomalies.length > 0 ? summary.anomalies.join(', ') : 'None detected'}`);
  }, []);

  // Filter entries
  const filteredEntries = filter
    ? entries.filter(e =>
        e.formatted.toLowerCase().includes(filter.toLowerCase()) ||
        e.layer.toLowerCase().includes(filter.toLowerCase())
      )
    : entries;

  if (!isVisible) return null;

  const getLayerColor = (layer: string) => {
    switch (layer) {
      case 'ACTION': return '#FF9800';
      case 'SENT':   return '#2196F3';
      case 'RECV':   return '#4CAF50';
      case 'ANIM':   return '#9C27B0';
      default:       return '#888';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '500px',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '11px',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '2px solid #333',
      }}
    >
      {/* Header */}
      <div style={{ padding: '8px', borderBottom: '1px solid #333', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', flex: 1 }}>DIAGNOSTIC LOG (Ctrl+Shift+D to hide)</span>
        <button onClick={handleClear} style={{ padding: '2px 8px', fontSize: '10px' }}>Clear</button>
        <button onClick={handleExport} style={{ padding: '2px 8px', fontSize: '10px' }}>Export</button>
        <button onClick={handleAnalyze} style={{ padding: '2px 8px', fontSize: '10px' }}>Analyze</button>
      </div>

      {/* Filter */}
      <div style={{ padding: '4px 8px', borderBottom: '1px solid #333', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Filter..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ flex: 1, padding: '2px 4px', fontSize: '10px', backgroundColor: '#222', color: '#fff', border: '1px solid #444' }}
        />
        <label style={{ fontSize: '10px' }}>
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
          />
          Auto-scroll
        </label>
        <span style={{ fontSize: '10px', color: '#888' }}>{filteredEntries.length} entries</span>
      </div>

      {/* Legend */}
      <div style={{ padding: '4px 8px', borderBottom: '1px solid #333', display: 'flex', gap: '12px', fontSize: '10px' }}>
        <span><span style={{ color: '#FF9800' }}>ACTION</span> = User/Autoplay</span>
        <span><span style={{ color: '#2196F3' }}>SENT</span> = To Server</span>
        <span><span style={{ color: '#4CAF50' }}>RECV</span> = From Server</span>
        <span><span style={{ color: '#9C27B0' }}>ANIM</span> = Visualized</span>
      </div>

      {/* Log entries */}
      <div
        ref={logRef}
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '4px 8px',
        }}
      >
        {filteredEntries.map((entry, i) => (
          <div
            key={i}
            style={{
              color: getLayerColor(entry.layer),
              whiteSpace: 'pre',
              lineHeight: 1.4,
            }}
          >
            {entry.formatted}
          </div>
        ))}
        {filteredEntries.length === 0 && (
          <div style={{ color: '#666', padding: '20px', textAlign: 'center' }}>
            No log entries yet.<br />
            Play a round to see diagnostic events.
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '4px 8px', borderTop: '1px solid #333', fontSize: '10px', color: '#666' }}>
        Console: window.diagLog.dump() | .export() | .summary() | .analyzeRound(N)
      </div>
    </div>
  );
}
